import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkle, CheckCircle, Crown, ShieldCheck, Clock, Broom } from "@phosphor-icons/react";

const TIER_ICONS = {
    essential: null,
    signature: <Sparkle size={14} weight="fill" className="text-gold" />,
    butler_priority: <Crown size={14} weight="fill" className="text-gold" />,
};

export default function PublicQuotePage() {
    const [catalog, setCatalog] = useState(null);
    const [inputs, setInputs] = useState({
        square_feet: 2000,
        frequency: "biweekly",
        service_tier: "signature",
        home_condition: "maintained",
        addons: {},
    });
    const [quoteData, setQuoteData] = useState(null);
    const [quoting, setQuoting] = useState(false);
    const [quoteError, setQuoteError] = useState(false);
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        http.get("/pricing/catalog").then((res) => setCatalog(res.data));
    }, []);

    useEffect(() => {
        if (!catalog) return;
        const timer = setTimeout(async () => {
            setQuoting(true);
            try {
                const addons = Object.entries(inputs.addons)
                    .filter(([, qty]) => qty > 0)
                    .map(([key, qty]) => ({ key, qty }));
                const { data } = await http.post("/pricing/quote", {
                    square_feet: inputs.square_feet,
                    frequency: inputs.frequency,
                    service_tier: inputs.service_tier,
                    home_condition: inputs.home_condition,
                    service_type: "recurring",
                    addons,
                });
                setQuoteData(data);
                setQuoteError(false);
            } catch (e) { setQuoteError(true); }
            finally { setQuoting(false); }
        }, 300);
        return () => clearTimeout(timer);
    }, [inputs, catalog]);

    function set(k, v) { setInputs((f) => ({ ...f, [k]: v })); }
    function setAddon(key, qty) { setInputs((f) => ({ ...f, addons: { ...f.addons, [key]: qty } })); }

    function bookNow() {
        localStorage.setItem("butler_quote_prefill", JSON.stringify(inputs));
        if (user) navigate("/tasks/new");
        else navigate("/register?next=/tasks/new");
    }

    if (!catalog) return <div className="max-w-5xl mx-auto px-6 py-16 text-slate-500">Loading…</div>;

    return (
        <div data-testid="public-quote-page" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
            <div className="label-eyebrow mb-3">/ Instant pricing</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mb-4">
                Your price in <span className="italic gold-text">30 seconds</span>.
            </h1>
            <p className="text-slate-400 text-base md:text-lg max-w-2xl mb-10 leading-relaxed">
                No phone calls. No account needed. Move the slider, pick your schedule, and see
                exactly what your home cleaning costs — the same transparent price every time.
            </p>

            <div className="grid lg:grid-cols-12 gap-8">
                <div className="lg:col-span-7 space-y-8">
                    <Field label={`Approximate home size: ${inputs.square_feet.toLocaleString()} sqft`}>
                        <input
                            data-testid="pq-sqft" type="range" min={400} max={8000} step={100}
                            value={inputs.square_feet}
                            onChange={(e) => set("square_feet", parseInt(e.target.value, 10))}
                            className="w-full accent-[#D4AF37]"
                        />
                        <div className="flex justify-between text-[10px] text-slate-500 mt-1">
                            <span>400</span><span>2,000</span><span>4,000</span><span>6,000</span><span>8,000+</span>
                        </div>
                    </Field>

                    <Field label="How often?">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {catalog.frequencies.map((freq) => {
                                const active = inputs.frequency === freq.key;
                                return (
                                    <button
                                        type="button" key={freq.key}
                                        data-testid={`pq-freq-${freq.key}`}
                                        onClick={() => set("frequency", freq.key)}
                                        className={`px-3 py-3 text-xs sm:text-sm rounded-sm border transition text-center ${
                                            active ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-slate-300 hover:border-white/25"
                                        }`}
                                    >
                                        <div className="font-medium">{freq.label}</div>
                                    </button>
                                );
                            })}
                        </div>
                    </Field>

                    <Field label="Home condition">
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {catalog.conditions.map((c) => {
                                const active = inputs.home_condition === c.key;
                                return (
                                    <button
                                        type="button" key={c.key}
                                        data-testid={`pq-cond-${c.key}`}
                                        onClick={() => set("home_condition", c.key)}
                                        className={`px-3 py-3 text-xs sm:text-sm rounded-sm border transition text-center ${
                                            active ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-slate-300 hover:border-white/25"
                                        }`}
                                    >
                                        <div className="font-medium">{c.key === "maintained" ? "Maintained" : c.label.split(" — ")[0]}</div>
                                    </button>
                                );
                            })}
                        </div>
                    </Field>

                    <Field label="Service tier">
                        <div className="space-y-2">
                            {catalog.tiers.map((tier) => {
                                const active = inputs.service_tier === tier.key;
                                const tierPrice = quoteData?.tier_ladder.find((t) => t.key === tier.key)?.final_price;
                                return (
                                    <div
                                        key={tier.key}
                                        data-testid={`pq-tier-${tier.key}-card`}
                                        className={`rounded-sm border transition ${active ? "border-gold bg-gold/10" : "border-white/10 hover:border-white/30"}`}
                                    >
                                        <button
                                            type="button"
                                            data-testid={`pq-tier-${tier.key}`}
                                            onClick={() => set("service_tier", tier.key)}
                                            className="w-full px-4 py-4 text-left flex items-center justify-between gap-3"
                                        >
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    {TIER_ICONS[tier.key]}
                                                    <span className={`font-serif text-base sm:text-lg ${active ? "text-gold" : "text-white"}`}>
                                                        {tier.label}
                                                    </span>
                                                    {tier.is_default && !active && <span className="text-[9px] uppercase tracking-widest text-slate-500">Most popular</span>}
                                                </div>
                                                <div className="text-xs text-slate-400 mt-1">{tier.tagline}</div>
                                            </div>
                                            <div className="text-right shrink-0">
                                                {tierPrice != null && (
                                                    <div className={`font-serif text-xl ${active ? "text-gold" : "text-slate-200"}`}>
                                                        ${tierPrice.toFixed(0)}
                                                    </div>
                                                )}
                                                <div className="text-[10px] uppercase tracking-widest text-slate-500">per visit</div>
                                            </div>
                                        </button>
                                        {active && tier.inclusions && (
                                            <div data-testid={`pq-tier-${tier.key}-inclusions`} className="px-4 pb-4 pt-1 border-t border-white/5">
                                                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2 mt-3">What&apos;s included</div>
                                                <ul className="space-y-1.5">
                                                    {tier.inclusions.map((line, i) => (
                                                        <li key={i} className="flex items-start gap-2 text-xs text-slate-300 leading-relaxed">
                                                            <CheckCircle size={12} weight="fill" className="text-gold mt-0.5 shrink-0" />
                                                            <span>{line}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </Field>

                    <Field label="Add-ons (à la carte)">
                        <div className="space-y-2">
                            {catalog.addons.map((a) => {
                                const qty = inputs.addons[a.key] || 0;
                                const isLaundry = a.key === "laundry_folding";
                                return (
                                    <div key={a.key} className="flex items-center justify-between gap-3 px-4 py-3 rounded-sm border border-white/10 bg-slate-900/40">
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm text-slate-100">{a.label}</div>
                                            <div className="text-[11px] text-slate-500 mt-0.5">${a.rate.toFixed(0)}{isLaundry ? " / load" : ""}</div>
                                        </div>
                                        {isLaundry ? (
                                            <div className="flex items-center gap-2">
                                                <button type="button" data-testid={`pq-addon-${a.key}-dec`} onClick={() => setAddon(a.key, Math.max(0, qty - 1))}
                                                    className="w-7 h-7 rounded-sm border border-white/10 hover:border-gold/40 text-slate-300">−</button>
                                                <span data-testid={`pq-addon-${a.key}-qty`} className="w-6 text-center text-sm">{qty}</span>
                                                <button type="button" data-testid={`pq-addon-${a.key}-inc`} onClick={() => setAddon(a.key, qty + 1)}
                                                    className="w-7 h-7 rounded-sm border border-white/10 hover:border-gold/40 text-slate-300">+</button>
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                data-testid={`pq-addon-${a.key}-toggle`}
                                                onClick={() => setAddon(a.key, qty > 0 ? 0 : 1)}
                                                className={`w-7 h-7 rounded-sm border transition ${
                                                    qty > 0 ? "border-gold bg-gold/15 text-gold" : "border-white/10 text-slate-500 hover:border-white/30"
                                                }`}
                                            >{qty > 0 ? <CheckCircle size={16} weight="fill" /> : "+"}</button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </Field>
                </div>

                {/* Sticky price panel */}
                <aside className="lg:col-span-5">
                    <div data-testid="pq-quote-summary" className="bg-slate-900/60 border border-white/10 p-6 rounded-sm lg:sticky lg:top-24">
                        <div className="label-eyebrow text-gold mb-3">/ Your price</div>
                        {quoting && !quoteData ? (
                            <div className="text-slate-500 text-sm italic py-8 text-center">Calculating…</div>
                        ) : quoteError && !quoteData ? (
                            <div data-testid="pq-quote-error" className="text-slate-400 text-sm py-8 text-center">
                                We couldn&apos;t reach our pricing engine. Adjust any input to retry.
                            </div>
                        ) : quoteData ? (
                            <>
                                <div className="flex items-end justify-between mb-1">
                                    <div className="text-xs text-slate-400 uppercase tracking-widest">{quoteData.quote.service_tier_label}</div>
                                    <div data-testid="pq-final-price" className="font-serif text-5xl gold-text leading-none">
                                        ${quoteData.quote.final_price.toFixed(0)}
                                    </div>
                                </div>
                                <div className="text-xs text-slate-500 text-right mb-5">per visit · ~{quoteData.quote.estimated_hours}h</div>

                                {quoteData.quote.addons.length > 0 && (
                                    <div className="space-y-1.5 text-xs text-slate-400 border-t border-white/5 pt-4 mb-4">
                                        <div className="text-[10px] uppercase tracking-widest text-slate-500">Add-ons</div>
                                        {quoteData.quote.addons.map((a) => (
                                            <div key={a.key} className="flex justify-between gap-2">
                                                <span>{a.label}{a.qty > 1 ? ` × ${a.qty}` : ""}</span>
                                                <span className="text-slate-200">+${a.total.toFixed(2)}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <button
                                    type="button"
                                    data-testid="pq-book-now"
                                    onClick={bookNow}
                                    className="btn-gold w-full text-center mt-2"
                                >
                                    Book this clean →
                                </button>
                                <p className="text-[11px] text-slate-500 text-center mt-3">
                                    Takes 2 minutes. No payment due until your visit is scheduled.
                                </p>

                                <div className="mt-6 pt-5 border-t border-white/5 space-y-3">
                                    <TrustLine icon={<ShieldCheck size={15} weight="fill" className="text-gold" />} text="Background-checked, vetted Butlers" />
                                    <TrustLine icon={<Broom size={15} weight="fill" className="text-gold" />} text="100% satisfaction — free re-clean within 24 hrs" />
                                    <TrustLine icon={<Clock size={15} weight="fill" className="text-gold" />} text="No contracts. Pause or cancel anytime" />
                                </div>
                            </>
                        ) : null}
                    </div>
                </aside>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-widest text-slate-400">{label}</label>
            <div className="mt-2">{children}</div>
        </div>
    );
}

function TrustLine({ icon, text }) {
    return (
        <div className="flex items-center gap-2.5 text-xs text-slate-300">
            {icon}<span>{text}</span>
        </div>
    );
}
